import React, { useMemo, useState, useCallback } from "react";
import { Grid, IconButton, Tooltip, Stack } from "@mui/material";
import { ContentCopy, Check } from "@mui/icons-material";
import CashDCard from "components/common/CashDCard";
import {
  StyledTitleTypography,
  StyledBodyTypography,
} from "../CompanyTab/CompanyInfo";
import { maskValue } from "utils/helper";
import { shallowEqual, useSelector } from "react-redux";

type DeveloperAccountSettingViewProps = {
  selectedCompanyId: string;
  shouldDisplayListing: boolean;
  apiKey: string;
  secret: string;
  accessToken: string;
  refreshToken: string;
};

const DeveloperAccountSettingView: React.FC<DeveloperAccountSettingViewProps> =
  React.memo(({ shouldDisplayListing, selectedCompanyId, apiKey, secret, accessToken, refreshToken }) => {
    const { systemCode } = useSelector(
      (state: any) => state.companyTabData,
      shallowEqual
    );
    const [copiedFormFieldStatus, setCopiedFormFieldStatus] = useState<
      Record<string, boolean>
    >({
      company_id: false,
      x_api_key: false,
      secret_key: false,
      access_token: false,
      refresh_token: false,
    });

    const hideKeysSystems = ["RECKON", "MYOBEXO", "XERO"];
    const isHideKeysSystem = hideKeysSystems.includes(systemCode);

    const fields = useMemo(
      () => {
        if (isHideKeysSystem) {
          return [
            { field: "company_id", value: selectedCompanyId },
            { field: "access_token", value: accessToken },
            { field: "refresh_token", value: refreshToken },
          ];
                  }

        return [
          { field: "company_id", value: selectedCompanyId },
          { field: "secret_key", value: secret },
          { field: "x_api_key", value: apiKey },
        ];
      },
      [selectedCompanyId, apiKey, secret, accessToken, refreshToken, isHideKeysSystem]
    );

    const handleCopy = useCallback(
      (field: string, text: string | null | undefined) => {
        setCopiedFormFieldStatus((prev) => ({ ...prev, [field]: true }));
        navigator.clipboard.writeText(text ?? "");
        setTimeout(() => {
          setCopiedFormFieldStatus((prev) => ({ ...prev, [field]: false }));
        }, 2000);
      },
      []
    );

    return (
      <CashDCard borderRadius="20px">
        {shouldDisplayListing && (
          <Grid container rowSpacing={1}>
            {fields.map(({ field, value }) => (
              <Grid item xs={6} px={5} key={field}>
                <StyledTitleTypography align="left">
                  {field.replaceAll("_", " ").toUpperCase()}
                </StyledTitleTypography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <StyledBodyTypography fontWeight={600}>
                    {maskValue(value, 40)}
                  </StyledBodyTypography>
                  {value && (
                    <Tooltip
                      title={
                        copiedFormFieldStatus[field]
                          ? "Copied"
                          : "Copy to clipboard"
                      }
                    >
                      <IconButton
                        onClick={() => handleCopy(field, value)}
                        size="small"
                      >
                        <StyledBodyTypography fontWeight={600} display="flex">
                          {copiedFormFieldStatus[field] ? (
                            <>
                              <Check fontSize="small" /> Copied
                            </>
                          ) : (
                            <>
                              <ContentCopy fontSize="small" /> Copy
                            </>
                          )}
                        </StyledBodyTypography>
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}
      </CashDCard>
    );
  });

export default DeveloperAccountSettingView;
